import React, { useState, useCallback } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Container, 
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  ListItemButton
} from '@mui/material';
import { 
  CheckCircle as CheckIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  ExpandMore as ExpandMoreIcon,
  Menu as MenuIcon,
  Lightbulb as ClarityIcon,
  Gavel as ResponsibilityIcon,
  RocketLaunch as ImpactIcon,
  Handshake as PartnershipIcon
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useThemeMode } from '../../common/contexts/ThemeContext';
import NavigationBar from '../../components/NavigationBar';

/**
 * Iris Homepage Component
 * 
 * A beautiful custom homepage showcasing Iris - The AI Strategist for Healthcare Delivery Organizations.
 * Features the complete content from the Gamma document with modern, responsive design.
 * 
 * Features:
 * - Hero section with Iris branding
 * - Problem/solution narrative
 * - Feature highlights and capabilities
 * - FAQ section with expandable answers
 * - Call-to-action sections
 * - Login functionality for dashboard access
 */
const GammaHomepage: React.FC = () => {
  const location = useLocation();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const [expandedFaq, setExpandedFaq] = useState<string | false>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Navigation items for internal page sections
  const navigationItems = [
    { label: 'Home', id: 'hero' },
    { label: 'Transformation', id: 'transformation' },
    { label: 'Who We Are', id: 'who-we-are' },
    { label: 'Mission', id: 'mission' },
    { label: 'Values', id: 'values' },
    { label: 'Leadership', id: 'leadership' },
    { label: 'Advisors', id: 'advisors' },
    { label: 'Why We Built This', id: 'why-we-built-this' },
    { label: 'FAQ', id: 'faq' },
    { label: 'Contact', id: 'contact' }
  ];

  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 80; // Approximate header height
      const elementPosition = element.offsetTop - headerHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  }, []);

  const handleFaqChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  // Theme-aware colors - matching landing page
  const bgColor = isDark ? '#000000' : '#F5F5F7';
  const textColor = isDark ? '#FFFFFF' : '#1D1D1F';
  const secondaryBg = isDark ? '#1a1a1a' : '#FFFFFF';
  const borderColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  const primaryBlue = '#007AFF';
  const lightBlue = '#7AF0FF';
  const darkBlue = isDark ? '#FFFFFF' : '#1D1D1F';
  const darkBlueText = isDark ? '#E0E0E0' : '#003D7A'; // For Garamond text sections
  const cardBg = isDark ? 'rgba(255, 255, 255, 0.05)' : '#FFFFFF';

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: bgColor,
      color: textColor,
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif',
      pt: 10, // Padding for fixed navigation bar
    }}>
      <NavigationBar />

      {/* Mobile Navigation Drawer for internal sections */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            background: secondaryBg,
            color: textColor,
            borderRight: `1px solid ${borderColor}`
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ 
            mb: 3, 
            fontWeight: 700,
            background: 'linear-gradient(135deg, #AF52DE 0%, #007AFF 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Navigation
          </Typography>
          <List>
            {navigationItems.map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton
                  onClick={() => scrollToSection(item.id)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    '&:hover': {
                      background: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                    }
                  }}
                >
                  <ListItemText 
                    primary={item.label}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: '1rem',
                        fontWeight: 500
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Hero Section */}
      <Box
        id="hero"
        sx={{
          pt: 4,
          pb: 6,
          background: bgColor,
          color: textColor,
          minHeight: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            {/* Iris Logo */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: { xs: 180, md: 160 },
                  height: { xs: 180, md: 160 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto'
                }}
              >
                <img 
                  src="/images/logo.png" 
                  alt="Iris Logo" 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              </Box>
            </Box>

            <Typography variant="body1" sx={{ 
              fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' },
              lineHeight: 1.5,
              fontWeight: 400,
              fontFamily: 'Garamond, "EB Garamond", serif',
              color: darkBlueText,
              maxWidth: 900,
              mx: 'auto',
              mt: { xs: 2, md: 3 },
              mb: 4,
              px: { xs: 2, sm: 0 }
            }}>
              Iris is the first AI-native strategy platform for healthcare organizations providing proactive, uniquely-tailored strategic recommendations, combining real-time data intelligence with AI-powered scenario modeling.
            </Typography>

            {/* Key Benefits Section */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="h3" sx={{ 
                fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.7rem' },
                fontWeight: 400,
                fontFamily: 'Garamond, "EB Garamond", serif',
                color: isDark ? '#7AF0FF' : primaryBlue,
                mb: 4,
                px: { xs: 2, sm: 0 }
              }}>
                We're putting healthcare leaders ahead of the game:
              </Typography>

              {/* Benefit Cards */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: { xs: 2, md: 3 }, 
                mb: 4,
                flexWrap: 'wrap',
                px: { xs: 2, sm: 0 }
              }}>
                {[
                  "Capture missed strategic opportunities",
                  "Optimize care delivery across every service line", 
                  "Unlock long-term sustainable success"
                ].map((benefit, index) => (
                  <Box
                    key={index}
                    sx={{
                      background: cardBg,
                      border: `1px solid ${borderColor}`,
                      borderRadius: 3,
                      p: { xs: 2.5, md: 3 },
                      width: { xs: '100%', sm: 'calc(50% - 8px)', md: 260 },
                      maxWidth: { xs: '100%', md: 260 },
                      textAlign: 'center',
                      backdropFilter: 'blur(20px)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                        border: `1px solid ${lightBlue}50`
                      }
                    }}
                  >
                    <Box sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #AF52DE 0%, #007AFF 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2
                    }}>
                      <CheckIcon sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Typography variant="body1" sx={{ 
                      color: textColor,
                      fontSize: { xs: '0.95rem', md: '1rem' },
                      fontWeight: 500,
                      lineHeight: 1.4
                    }}>
                      {benefit}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Call-to-Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => window.open('mailto:info@iris.healthcare?subject=Book a Demo - Iris Platform&body=Hello, I would like to schedule a demo of the Iris platform. Please let me know your availability.', '_blank')}
                  sx={{
                    background: 'linear-gradient(135deg, #AF52DE 0%, #007AFF 100%)',
                    color: 'white',
                    px: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 3,
                    textTransform: 'none',
                    boxShadow: '0 4px 20px rgba(0, 122, 255, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 32px rgba(0, 122, 255, 0.4)'
                    }
                  }}
                >
                  Book a Demo
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    border: '2px solid #7AF0FF',
                    color: '#7AF0FF',
                    px: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 3,
                    textTransform: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'rgba(122, 240, 255, 0.1)',
                      border: '2px solid #7AF0FF',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Transformation Section - Moved to top */}
      <Box id="transformation" sx={{ py: 8, bgcolor: secondaryBg }}>
        <Container maxWidth="lg">
            <Typography variant="h4" sx={{ 
              textAlign: 'center', 
            fontWeight: 400,
            fontFamily: 'Garamond, "EB Garamond", serif',
              mb: 8,
            color: darkBlueText,
              fontSize: { xs: '1.8rem', md: '2.2rem' },
              letterSpacing: '-0.01em'
            }}>
              From Months to Minutes: The Iris Transformation
            </Typography>
            
            <Grid container spacing={8}>
              {/* Left Column - Traditional Process */}
              <Grid item xs={12} md={6}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 400,
                  fontFamily: 'Garamond, "EB Garamond", serif',
                  mb: 6, 
                  color: darkBlueText,
                  fontSize: '1.5rem',
                  textAlign: 'center'
                }}>
                  Traditional Process: 6+ Months
                </Typography>
                
                {/* Clean timeline with light grey nodes */}
                <Box sx={{ position: 'relative', pl: 4 }}>
                  {/* Vertical timeline line */}
                  <Box sx={{
                    position: 'absolute',
                    left: 20,
                    top: 0,
                    bottom: 0,
                    width: '1px',
                    background: isDark ? '#b0b0b0' : '#999999'
                  }} />
                  
                  {/* Timeline items */}
                  <Box sx={{ mb: 5, position: 'relative' }}>
                    <Box sx={{
                      position: 'absolute',
                      left: -20,
                      top: 8,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: isDark ? '#b0b0b0' : '#999999'
                    }} />
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 400,
                        fontFamily: 'Garamond, "EB Garamond", serif',
                        mb: 1, 
                        color: darkBlueText,
                        fontSize: '1.1rem'
                      }}>
                        Data Gathering + Cleanup
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: isDark ? '#b0b0b0' : '#666666',
                        lineHeight: 1.5,
                        fontSize: '0.9rem'
                      }}>
                        Weeks spent manually collecting and cleaning fragmented data sources
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ mb: 5, position: 'relative' }}>
                    <Box sx={{
                      position: 'absolute',
                      left: -20,
                      top: 8,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: isDark ? '#b0b0b0' : '#999999'
                    }} />
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 400,
                        fontFamily: 'Garamond, "EB Garamond", serif',
                        mb: 1, 
                        color: darkBlueText,
                        fontSize: '1.1rem'
                      }}>
                        Analysis & Opportunity ID
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: isDark ? '#b0b0b0' : '#666666',
                        lineHeight: 1.5,
                        fontSize: '0.9rem'
                      }}>
                        Lengthy analysis phase with static datasets and delayed insights
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ mb: 5, position: 'relative' }}>
                    <Box sx={{
                      position: 'absolute',
                      left: -20,
                      top: 8,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: isDark ? '#b0b0b0' : '#999999'
                    }} />
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 400,
                        fontFamily: 'Garamond, "EB Garamond", serif',
                        mb: 1, 
                        color: darkBlueText,
                        fontSize: '1.1rem'
                      }}>
                        Scenario Modeling
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: isDark ? '#b0b0b0' : '#666666',
                        lineHeight: 1.5,
                        fontSize: '0.9rem'
                      }}>
                        Time-intensive manual modeling with limited scenario testing
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ mb: 5, position: 'relative' }}>
                    <Box sx={{
                      position: 'absolute',
                      left: -20,
                      top: 8,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: isDark ? '#b0b0b0' : '#999999'
                    }} />
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 400,
                        fontFamily: 'Garamond, "EB Garamond", serif',
                        mb: 1, 
                        color: darkBlueText,
                        fontSize: '1.1rem'
                      }}>
                        Testing & Revisions
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: isDark ? '#b0b0b0' : '#666666',
                        lineHeight: 1.5,
                        fontSize: '0.9rem'
                      }}>
                        Multiple revision cycles as data becomes stale
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ mb: 5, position: 'relative' }}>
                    <Box sx={{
                      position: 'absolute',
                      left: -20,
                      top: 8,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: isDark ? '#b0b0b0' : '#999999'
                    }} />
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 400,
                        fontFamily: 'Garamond, "EB Garamond", serif',
                        mb: 1, 
                        color: darkBlueText,
                        fontSize: '1.1rem'
                      }}>
                        Business Case Presentation
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: isDark ? '#b0b0b0' : '#666666',
                        lineHeight: 1.5,
                        fontSize: '0.9rem'
                      }}>
                        Manual presentation development with outdated assumptions
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Bottom separator line */}
                  <Box sx={{
                    height: '1px',
                    background: isDark ? '#b0b0b0' : '#999999',
                    mt: 2
                  }} />
                </Box>
              </Grid>
              
              {/* Right Column - With Iris */}
              <Grid item xs={12} md={6}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 600, 
                  mb: 6, 
                  color: '#ffffff',
                  fontSize: '1.5rem',
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #AF52DE 0%, #007AFF 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  With Iris: An Afternoon
                </Typography>
                
                {/* Numbered steps with gradient lines above numbers */}
                <Box>
                  {/* Step 01 */}
                  <Box sx={{ mb: 6 }}>
                    <Box sx={{
                      height: '1px',
                      background: 'linear-gradient(90deg, #AF52DE 0%, #7AF0FF 100%)',
                      mb: 2
                    }} />
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                      <Box sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 3,
                        flexShrink: 0
                      }}>
                        <Typography sx={{ 
                          color: '#ffffff', 
                          fontSize: '0.9rem', 
                          fontWeight: 600 
                        }}>
                          01
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 400,
                          fontFamily: 'Garamond, "EB Garamond", serif',
                          mb: 2, 
                          color: isDark ? '#7AF0FF' : primaryBlue,
                          fontSize: '1.1rem'
                        }}>
                          Identifying Strategic Opportunities
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: darkBlue,
                          fontFamily: 'Garamond, "EB Garamond", serif',
                          fontWeight: 400,
                          lineHeight: 1.5,
                          fontSize: '0.9rem'
                        }}>
                          AI-powered analysis automatically identifies opportunities proactively via integrated real-time data (financial, clinical, operational, patient populations, market intel)
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  {/* Step 02 */}
                  <Box sx={{ mb: 6 }}>
                    <Box sx={{
                      height: '1px',
                      background: 'linear-gradient(90deg, #AF52DE 0%, #7AF0FF 100%)',
                      mb: 2
                    }} />
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                      <Box sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 3,
                        flexShrink: 0
                      }}>
                        <Typography sx={{ 
                          color: '#ffffff', 
                          fontSize: '0.9rem', 
                          fontWeight: 600 
                        }}>
                          02
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 400,
                          fontFamily: 'Garamond, "EB Garamond", serif',
                          mb: 2, 
                          color: isDark ? '#7AF0FF' : primaryBlue,
                          fontSize: '1.1rem'
                        }}>
                          Rapid Scenario Testing
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: darkBlue,
                          fontFamily: 'Garamond, "EB Garamond", serif',
                          fontWeight: 400,
                          lineHeight: 1.5,
                          fontSize: '0.9rem'
                        }}>
                          Interactive scenario modeling in a 3D 'sandbox' environment enables testing of unlimited scenarios with immediate results
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  {/* Step 03 */}
                  <Box sx={{ mb: 6 }}>
                    <Box sx={{
                      height: '1px',
                      background: 'linear-gradient(90deg, #AF52DE 0%, #7AF0FF 100%)',
                      mb: 2
                    }} />
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                      <Box sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 3,
                        flexShrink: 0
                      }}>
                        <Typography sx={{ 
                          color: '#ffffff', 
                          fontSize: '0.9rem', 
                          fontWeight: 600 
                        }}>
                          03
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 400,
                          fontFamily: 'Garamond, "EB Garamond", serif',
                          mb: 2, 
                          color: isDark ? '#7AF0FF' : primaryBlue,
                          fontSize: '1.1rem'
                        }}>
                          Business Case Generated
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: darkBlue,
                          fontFamily: 'Garamond, "EB Garamond", serif',
                          fontWeight: 400,
                          lineHeight: 1.5,
                          fontSize: '0.9rem'
                        }}>
                          Dynamic, immersive presentations with current data and validated projections
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                
                {/* The Result */}
                <Box sx={{ mt: 6, textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 700, 
                    color: '#ffffff',
                    fontSize: '1.3rem',
                    background: 'linear-gradient(135deg, #AF52DE 0%, #007AFF 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    lineHeight: 1.3
                  }}>
                    The Result: a proactive + continuously optimizing healthcare delivery business.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Galaxy Image Section - Under Transformation */}
        <Box sx={{ py: 4, bgcolor: bgColor }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center' }}>
              <img 
                src="/images/galaxy.png" 
                alt="Galaxy" 
                style={{
                  width: '100%',
                  height: 'auto',
                  maxWidth: '1200px',
                  borderRadius: '8px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                }}
              />
          </Box>
        </Container>
      </Box>

        {/* About Us Sections */}
        {/* Who We Are */}
        <Box id="who-we-are" sx={{ py: 8, bgcolor: secondaryBg }}>
          <Container maxWidth="lg">
            <Typography variant="h3" component="h2" sx={{ 
              textAlign: 'center', 
              fontWeight: 700, 
              mb: 4,
              background: 'linear-gradient(135deg, #AF52DE 0%, #007AFF 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '2rem', md: '2.5rem' },
              letterSpacing: '-0.01em',
              lineHeight: 1.2
            }}>
              Who We Are
            </Typography>
            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 0 } }}>
              <Typography variant="body1" sx={{ 
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                lineHeight: 1.6,
                fontWeight: 400,
                fontFamily: 'Garamond, "EB Garamond", serif',
                color: darkBlueText,
                mb: 3
              }}>
                We build technology that helps healthcare organizations make smarter decisions, faster. Our platform brings together real-time market intelligence, operational datasets, and advanced AI agents to answer the questions that matter most in healthcare strategy—where to grow, how to compete, how to staff, how to optimize resources, and how to plan for the future.
              </Typography>
              <Typography variant="body1" sx={{ 
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                lineHeight: 1.6,
                fontWeight: 400,
                fontFamily: 'Garamond, "EB Garamond", serif',
                color: darkBlueText,
                mb: 3
              }}>
                We combine deep industry experience with state-of-the-art machine learning to give leaders clarity in an environment that is often noisy, fragmented, and difficult to navigate.
              </Typography>
              <Typography variant="h5" sx={{
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                fontWeight: 700,
                color: primaryBlue,
                mb: 3,
                textAlign: 'center',
                fontFamily: 'Garamond, "EB Garamond", serif'
              }}>
                AI should be practical, reliable, and actionable.
              </Typography>
              <Typography variant="body1" sx={{ 
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                lineHeight: 1.6,
                fontWeight: 400,
                fontFamily: 'Garamond, "EB Garamond", serif',
                color: darkBlueText,
                mb: 3
              }}>
                Every feature in our system is designed around accuracy, transparency, and measurable results. That includes site selection, capital planning, workforce strategy, competitive intelligence, value-based care modeling, payer strategy, market expansion, and continuous performance improvement.
              </Typography>
              <Typography variant="body1" sx={{ 
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                lineHeight: 1.6,
                fontWeight: 400,
                fontFamily: 'Garamond, "EB Garamond", serif',
                color: darkBlueText
              }}>
                We're here to give healthcare organizations the intelligence they need to deliver better care, operate more efficiently, and grow with confidence.
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Our Mission */}
        <Box id="mission" sx={{ py: 8, bgcolor: bgColor }}>
          <Container maxWidth="lg">
            <Typography variant="h3" component="h2" sx={{ 
              textAlign: 'center', 
              fontWeight: 700, 
              mb: 4,
              background: 'linear-gradient(135deg, #AF52DE 0%, #007AFF 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '2rem', md: '2.5rem' },
              letterSpacing: '-0.01em',
              lineHeight: 1.2
            }}>
              Our Mission
            </Typography>
            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 0 } }}>
              <Typography variant="body1" sx={{ 
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                lineHeight: 1.6,
                fontWeight: 400,
                fontFamily: 'Garamond, "EB Garamond", serif',
                color: darkBlueText,
                textAlign: 'center'
              }}>
                To make healthcare strategy data-driven, accessible, and future-proof by combining industry expertise with modern AI—so organizations can act quickly, reduce uncertainty, and plan with precision.
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Our Values */}
        <Box id="values" sx={{ py: 8, bgcolor: secondaryBg }}>
          <Container maxWidth="lg">
            <Typography variant="h3" component="h2" sx={{ 
              textAlign: 'center', 
              fontWeight: 700, 
              mb: 6,
              background: 'linear-gradient(135deg, #AF52DE 0%, #007AFF 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '2rem', md: '2.5rem' },
              letterSpacing: '-0.01em',
              lineHeight: 1.2
            }}>
              Our Values
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              {[
                { icon: <ClarityIcon sx={{ fontSize: 40, color: primaryBlue }} />, title: 'Clarity', description: 'We transform fragmented information into simple, decision-ready insights.' },
                { icon: <ResponsibilityIcon sx={{ fontSize: 40, color: primaryBlue }} />, title: 'Responsibility', description: 'We design AI with governance and compliance at the core. No black boxes.' },
                { icon: <ImpactIcon sx={{ fontSize: 40, color: primaryBlue }} />, title: 'Impact', description: 'Every feature is built to create measurable operational and financial improvements.' },
                { icon: <PartnershipIcon sx={{ fontSize: 40, color: primaryBlue }} />, title: 'Partnership', description: 'We work alongside healthcare leaders, not above them.' },
              ].map((value, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card sx={{
                    height: '100%',
                    p: 3,
                    backgroundColor: cardBg,
                    borderRadius: 4,
                    border: `1px solid ${borderColor}`,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(0, 122, 255, 0.2)',
                      borderColor: primaryBlue,
                    },
                    textAlign: 'center',
                  }}>
                    <CardContent>
                      <Box sx={{ mb: 2 }}>{value.icon}</Box>
                      <Typography variant="h6" sx={{
                        fontWeight: 600,
                        color: darkBlue,
                        mb: 1.5,
                        fontSize: '1.1rem',
                      }}>
                        {value.title}
                      </Typography>
                      <Typography variant="body2" sx={{
                        color: darkBlueText,
                        lineHeight: 1.7,
                        fontSize: '0.95rem',
                        fontFamily: 'Garamond, "EB Garamond", serif',
                        fontWeight: 400,
                      }}>
                        {value.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

      {/* Leadership Section - Using Original Founders Content */}
      <Box id="leadership" sx={{ py: 8, bgcolor: bgColor }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" sx={{ 
            textAlign: 'center', 
            fontWeight: 700, 
            mb: 2,
            background: 'linear-gradient(135deg, #AF52DE 0%, #007AFF 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontSize: { xs: '2rem', md: '2.5rem' },
            letterSpacing: '-0.01em',
            lineHeight: 1.2
          }}>
            Leadership
          </Typography>
          
          <Typography variant="h6" sx={{ 
            textAlign: 'center', 
            mb: 6,
            color: isDark ? '#b0b0b0' : '#666666',
            fontFamily: 'Garamond, "EB Garamond", serif',
            fontWeight: 400,
            fontSize: '1.2rem',
            maxWidth: 600,
            mx: 'auto'
          }}>
            Combining decades of healthcare leadership and AI innovation to redefine optimization.
          </Typography>

          <Grid container spacing={6} justifyContent="center">
            {/* David Reich - CEO */}
            <Grid item xs={12} md={8}>
              <Box sx={{ textAlign: 'center' }}>
                {/* Placeholder for David's photo */}
                <Box sx={{ 
                  width: 200, 
                  height: 200, 
                  borderRadius: '50%', 
                  background: 'linear-gradient(135deg, #AF52DE 0%, #007AFF 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 4,
                  boxShadow: '0 8px 32px rgba(175, 82, 222, 0.3)'
                }}>
                  <Typography sx={{ color: 'white', fontSize: '24px', fontWeight: 600 }}>
                    DR
                  </Typography>
                </Box>
                
                <Typography variant="h5" sx={{ 
                  fontWeight: 400,
                  fontFamily: 'Garamond, "EB Garamond", serif',
                  mb: 1,
                  color: darkBlueText,
                  fontSize: '1.5rem'
                }}>
                  David Reich
                </Typography>
                
                <Typography variant="h6" sx={{ 
                  mb: 3,
                  background: 'linear-gradient(135deg, #AF52DE 0%, #007AFF 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: '1.1rem',
                  fontWeight: 600
                }}>
                  Co-Founder & Chief Executive Officer
                </Typography>
                
                <Typography variant="body1" sx={{ 
                  color: isDark ? '#b0b0b0' : '#666666',
                  fontFamily: 'Garamond, "EB Garamond", serif',
                  fontWeight: 400,
                  lineHeight: 1.6,
                  textAlign: 'left',
                  mb: 2
                }}>
                  David is the strategic architect who brings over a decade of executive-level experience driving financial performance, operational excellence, and complex strategy within the nation's largest and most sophisticated health systems. He has a proven track record of converting strategic vision into measurable value.
                </Typography>
                
                <Typography variant="body1" sx={{ 
                  color: isDark ? '#b0b0b0' : '#666666',
                  fontFamily: 'Garamond, "EB Garamond", serif',
                  fontWeight: 400,
                  lineHeight: 1.6,
                  textAlign: 'left',
                  mb: 2
                }}>
                  <strong>Executive Scale & Impact:</strong> As a pivotal strategic planning partner within a foremost healthcare delivery organization, he architected and executed high-impact market and real estate strategies across a multi-million square foot healthcare portfolio, overseeing capital commitments exceeding $500MM.
                </Typography>
                
                <Typography variant="body1" sx={{ 
                  color: isDark ? '#b0b0b0' : '#666666',
                  fontFamily: 'Garamond, "EB Garamond", serif',
                  fontWeight: 400,
                  lineHeight: 1.6,
                  textAlign: 'left',
                  mb: 2
                }}>
                  <strong>Strategic Mastery:</strong> His meticulously comprehensive, data-driven approach was instrumental in successfully leading dozens of complex, large-scale initiatives. These included critical expansions, strategic consolidations, supporting intricate M&A transactions, and advanced service line optimizations across the full spectrum of facility types—from multi-specialty medical centers to Ambulatory Surgical Centers (ASCs) and primary care clinics.
                </Typography>
                
                <Typography variant="body1" sx={{ 
                  color: '#b0b0b0', 
                  lineHeight: 1.6,
                  textAlign: 'left'
                }}>
                  <strong>The Vision:</strong> David's deep, real-world understanding of the US healthcare system's operational and market complexities is the foundation of Iris. He is singularly focused on empowering HDOs with precise, timely, AI-driven recommendations that are contextually relevant, enabling them to optimize care delivery, navigate a turbulent market, and achieve truly sustainable, impactful growth.
                </Typography>
              </Box>
            </Grid>

            {/* Marc Sharma - CTO (hidden; restore this block to show on /about Leadership section) */}
            {/*
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ 
                  width: 200, 
                  height: 200, 
                  borderRadius: '50%', 
                  background: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 4,
                  boxShadow: '0 8px 32px rgba(0, 122, 255, 0.3)'
                }}>
                  <Typography sx={{ color: 'white', fontSize: '24px', fontWeight: 600 }}>
                    MS
                  </Typography>
                </Box>
                
                <Typography variant="h5" sx={{ 
                  fontWeight: 400,
                  fontFamily: 'Garamond, "EB Garamond", serif',
                  mb: 1,
                  color: darkBlueText,
                  fontSize: '1.5rem'
                }}>
                  Dr. Marc G. Sharma
                </Typography>
                
                <Typography variant="h6" sx={{ 
                  mb: 3,
                  background: 'linear-gradient(135deg, #AF52DE 0%, #007AFF 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: '1.1rem',
                  fontWeight: 600
                }}>
                  Co-Founder & Chief Technology Officer
                </Typography>
                
                <Typography variant="body1" sx={{ 
                  color: isDark ? '#b0b0b0' : '#666666',
                  fontFamily: 'Garamond, "EB Garamond", serif',
                  fontWeight: 400,
                  lineHeight: 1.6,
                  textAlign: 'left',
                  mb: 2
                }}>
                  Marc Gareth Sharma, Ph.D. — Co-founder and Chief Architect at Iris. Marc built Iris's AI intelligence layer and data systems, translating strategy into self-learning, high-performance intelligence for healthcare.
                </Typography>
                
                <Typography variant="body1" sx={{ 
                  color: isDark ? '#b0b0b0' : '#666666',
                  fontFamily: 'Garamond, "EB Garamond", serif',
                  fontWeight: 400,
                  lineHeight: 1.6,
                  textAlign: 'left',
                  mb: 2
                }}>
                  Previously Director of AI/ML Products at HireRight Global, he led a 4-continent roadmap that cut processing times by 30%, reduced overhead 15%, and drove double-digit revenue growth through responsible AI.
                </Typography>
                
                <Typography variant="body1" sx={{ 
                  color: '#b0b0b0', 
                  lineHeight: 1.6,
                  textAlign: 'left'
                }}>
                  A self proclaimed 'Husband, Father, Dreamer and Tinkerer',  Marc's career bridges media, enterprise, and medicine — all unified by one mission: turning advanced AI into clarity, scalability, and measurable value.
                </Typography>
                
                <Typography variant="body2" sx={{ 
                  color: '#888888', 
                  lineHeight: 1.5,
                  textAlign: 'left',
                  mt: 3,
                  fontSize: '0.9rem',
                  fontStyle: 'italic',
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                  pt: 2
                }}>
                  Marc earned his Ph.D. in Aerospace Engineering from the Technion – Israel Institute of Technology, an M.Sc. from the University of Virginia, and a B.Sc. in Computational Mathematics from CUNY.
                </Typography>
              </Box>
            </Grid>
            */}
          </Grid>

        </Container>
      </Box>

      {/* Advisors Section */}
      <Box id="advisors" sx={{ py: 8, bgcolor: secondaryBg }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" sx={{ 
            textAlign: 'center', 
            fontWeight: 700, 
            mb: 6,
            background: 'linear-gradient(135deg, #AF52DE 0%, #007AFF 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontSize: { xs: '2rem', md: '2.5rem' },
            letterSpacing: '-0.01em',
            lineHeight: 1.2
          }}>
            Advisors
          </Typography>
          <Box sx={{ maxWidth: 600, mx: 'auto', textAlign: 'center', px: { xs: 2, sm: 0 } }}>
            <Typography variant="body1" sx={{ 
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              lineHeight: 1.6,
              fontWeight: 400,
              fontFamily: 'Garamond, "EB Garamond", serif',
              color: darkBlueText
            }}>
              Under Selection
          </Typography>
          </Box>
        </Container>
      </Box>

      {/* Why We Built This */}
      <Box id="why-we-built-this" sx={{ py: 8, bgcolor: bgColor }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" sx={{ 
            textAlign: 'center', 
              fontWeight: 700, 
              mb: 4, 
              background: 'linear-gradient(135deg, #AF52DE 0%, #007AFF 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            fontSize: { xs: '2rem', md: '2.5rem' },
            letterSpacing: '-0.01em',
            lineHeight: 1.2
            }}>
            Why We Built This
            </Typography>
          <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 0 } }}>
            <Typography variant="body1" sx={{ 
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              lineHeight: 1.6,
              fontWeight: 400,
              fontFamily: 'Garamond, "EB Garamond", serif',
              color: darkBlueText,
              textAlign: 'center'
            }}>
              Healthcare leaders make decisions that are expensive, high-stakes, and often based on outdated or incomplete information. We built this company to change that—by giving organizations a clear, data-driven view of their markets, their operations, and their opportunities. Our goal is to move the industry from guesswork to intelligence.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box id="faq" sx={{ py: 8, bgcolor: secondaryBg }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" sx={{ 
            textAlign: 'center', 
            fontWeight: 700, 
            mb: 6,
            background: 'linear-gradient(135deg, #AF52DE 0%, #007AFF 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontSize: { xs: '2rem', md: '2.5rem' },
            letterSpacing: '-0.01em',
            lineHeight: 1.2
          }}>
            FAQ
          </Typography>
          
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            {[
              {
                question: "What makes Iris different from all the other healthcare analytics and business intelligence (BI) platforms out there?",
                answer: "Think of other platforms as a largely passive map, and Iris as a proactive GPS. Many existing tools are just dashboards that show you what happened in the past. We're a true AI co-pilot that actively helps you plan for the future. Our core differentiators are the 3D geospatial sandbox where you can actually simulate changes and see the real-time impact, our AI's ability to predict how competitors and patient populations will react to your moves, and our proprietary Care Score that gives you a single metric to benchmark your performance on both quality and access. This combination is completely unique."
              },
              {
                question: "How does Iris help us make money and save money? What's the ROI?",
                answer: "The ROI is really measurable and we focus on it constantly. We help you increase revenue by quickly identifying the best market expansion opportunities and optimizing your service lines, which can lead to a 10-25%+ increase in patient capture. We also help you prevent revenue loss by reducing patient leakage. On the cost savings side, we cut strategic planning cycles from months to a couple of weeks (max), saving you a ton on labor and expensive consultants. Our platform can also lead to a 15-30%+ reduction in operational costs through things like optimizing provider schedules and resource allocation."
              },
              {
                question: "How does Iris handle data privacy and security, especially with PHI?",
                answer: "We get this question all the time, and it's a top priority for us. Our platform is designed with a Privacy-First Design approach from the ground up, not as an afterthought. We use advanced de-identification techniques like tokenization and aggregation to make sure all Protected Health Information (PHI) is anonymized before it's ever used for our models. Our security foundations actually exceed HIPAA, SOC 2 Type II, and NIST 800-53 standards. Everything is encrypted with FIPS 140-2 validated encryption (AES-256), and we have role-based access controls and immutable audit logs."
              },
              {
                question: "The healthcare IT landscape is a nightmare. How can you promise a 'plug-and-play' experience?",
                answer: "Interoperability is a big challenge in healthcare - that's why we built our Adaptive Integration framework. It's a two-part system: first, we've incorporated pre-built connectors for the top 10 EHR and RCM systems, which covers about 75% of hospitals and health systems. Second, Iris uses an AI-powered mapping engine that intelligently figures out data schemas. This means we can integrate with less common or even legacy systems in weeks, not months, so you don't have to do a ton of manual data entry."
              },
              {
                question: "I'm a smaller practice owner/group. Why should I pay a premium for Iris when I can just use existing free or low-cost tools?",
                answer: "That's a fair question. The difference is the value and speed to action. While some tools give you basic market data, they don't actually help you plan your next move. Iris provides you with a direct path from insight to action in minutes. For a smaller practice group, this means you can quickly model the impact of hiring a new provider, deciding whether to relocate, or responding to a new competitor without hiring expensive consultants or waiting months for a report. The cost of making a single wrong strategic move far outweighs the investment in Iris."
              },
              {
                question: "How does Iris help us understand and plan for the patient populations we serve?",
                answer: "Iris goes way beyond basic demographics. Our predictive engine is built on a multi-dimensional framework that considers a ton of factors and millions of individual datapoints to create a truly comprehensive picture. It includes everything from hyperlocal, geospatial data like real estate, transportation, and traffic patterns, to socioeconomic details like income and education. We also analyze a patient's historical behavior, their referral patterns, and even online sentiment about providers. This allows us to give you a really precise, hyper-local prediction of where specific patient cohorts are likely to seek care - and why - so you can tailor your services and outreach to what they actually need, where they need it, and when they need it."
              },
              {
                question: "We're a regional health system considering a merger with another smaller system. How can Iris help us with that?",
                answer: "That's a perfect use case for Iris. Mergers and acquisitions are huge, high-stakes decisions. Iris acts like your AI-powered due diligence team. You can integrate data from both organizations and visualize the combined network in our 3D environment. We help you identify potential overlaps in service areas, spot patient leakage, and find opportunities for consolidation and synergies. Our AI can even model different scenarios—like full or partial integration—and predict the financial outcomes, P&L impacts, cost savings, and risks of each one. It's all about giving you the data to make a confident decision on a multi-million-dollar deal."
              },
              {
                question: "How does Iris help us deal with payors and optimize our contracting strategy?",
                answer: "Iris provides a unique advantage in the real of payor contracting. We can model the impact of different payor contract terms, like reimbursement rates and co-pays, on patient choice and your overall revenue. By analyzing your local market, Iris can show you what kind of payor mix is most common for specific patient cohorts in certain areas. You can simulate the effect of signing a new Value-Based Care contract, for example, and see how it might change your financial performance and your Care Score. This helps you go into negotiations with a ton of data and a clear strategy to attract the patient populations you want."
              },
              {
                question: "Our organization is already buried in data from our EHR, billing, and other systems. How do you prevent Iris from becoming just another data silo?",
                answer: "This a huge problem we see everywhere in the world of healthcare data. Iris is designed to be the opposite of a silo. We don't just dump more data on your desk. Instead, we act as a unifying layer that connects your existing systems. Our AI-powered engine takes that fragmented, raw data from your EHR, RCM, and other sources, and then it proactively translates it into actionable, real-world insights and recommendations. You're not looking at more dashboards; you're looking at clear, direct answers to your biggest strategic questions, all in one place - and instantly at the tips of your fingers."
              }
            ].map((faq, index) => (
              <Accordion
                key={index}
                expanded={expandedFaq === `panel${index}`}
                onChange={handleFaqChange(`panel${index}`)}
                sx={{ 
                  mb: 2,
                  background: cardBg,
                  border: `1px solid ${borderColor}`,
                  borderRadius: 3,
                  '&:before': {
                    display: 'none'
                  },
                  '&.Mui-expanded': {
                    background: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 122, 255, 0.05)'
                  }
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon sx={{ color: '#007AFF' }} />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      margin: '16px 0'
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600, 
                    background: 'linear-gradient(135deg, #AF52DE 0%, #007AFF 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" sx={{ color: isDark ? '#b0b0b0' : '#666666', lineHeight: 1.6 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box id="contact" sx={{ 
        py: 8, 
        background: isDark 
          ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)'
          : 'linear-gradient(135deg, #F5F5F7 0%, #E5E5E7 50%, #F5F5F7 100%)', 
        color: darkBlue,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 70% 30%, rgba(0, 122, 255, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none'
        }
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            {/* Iris Logo */}
            <Box sx={{ mb: 6 }}>
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto'
                }}
              >
                <img 
                  src="/images/logo.png" 
                  alt="Iris Logo" 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              </Box>
            </Box>

            <Typography variant="h3" component="h2" sx={{ 
              fontWeight: 700, 
              mb: 4,
              fontSize: { xs: '2rem', md: '2.5rem' },
              letterSpacing: '-0.01em',
              lineHeight: 1.2,
              background: 'linear-gradient(135deg, #AF52DE 0%, #007AFF 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Ready to Transform Care Delivery?
            </Typography>
            <Typography variant="h6" sx={{ 
              mb: 6, 
              opacity: 0.9, 
              maxWidth: 800, 
              mx: 'auto',
              fontSize: '1.3rem',
              lineHeight: 1.4,
              fontFamily: 'Garamond, "EB Garamond", serif',
              fontWeight: 400,
              color: isDark ? '#e0e0e0' : darkBlueText
            }}>
              See what the first AI-native strategy platform can do for your healthcare organization.
            </Typography>
            <Typography variant="body1" sx={{ 
              mb: 6, 
              opacity: 0.8, 
              maxWidth: 900, 
              mx: 'auto',
              fontSize: '1.1rem',
              lineHeight: 1.5,
              fontFamily: 'Garamond, "EB Garamond", serif',
              fontWeight: 400,
              color: isDark ? '#b0b0b0' : '#666666'
            }}>
              Join the healthcare organizations already leveraging AI-powered strategic intelligence to capture missed opportunities and optimize care delivery. 
              Our team of healthcare strategy experts and AI technologists is ready to demonstrate how Iris can transform your planning process from reactive to proactive.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 6 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => window.open('mailto:info@iris.healthcare?subject=Book a Demo - Iris Platform&body=Hello, I would like to schedule a demo of the Iris platform. Please let me know your availability.', '_blank')}
                sx={{
                  px: 4,
                  py: 2.5,
                  borderRadius: 3,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)',
                  boxShadow: '0 8px 30px rgba(0, 122, 255, 0.3)',
                  border: 'none',
                  textTransform: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0056CC 0%, #4A4AC7 100%)',
                    boxShadow: '0 12px 40px rgba(0, 122, 255, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Book a Demo
              </Button>
            </Box>

            <Typography variant="h6" sx={{ 
              mb: 3, 
              fontWeight: 600,
              background: 'linear-gradient(135deg, #AF52DE 0%, #007AFF 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: '1.2rem'
            }}>
              Built for Healthcare Leaders At:
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              {[
                'Health Systems & Hospital Networks',
                'Ambulatory Surgical Centers',
                'Clinic Networks & Physician Groups',
                'Accountable Care Organizations',
                'Primary & Specialty Care Organizations'
              ].map((org, index) => (
                <Chip
                  key={index}
                  label={org}
                  sx={{
                    background: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 61, 122, 0.1)',
                    color: darkBlue,
                    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 61, 122, 0.2)'}`,
                    borderRadius: 2,
                    fontWeight: 400,
                    fontFamily: 'Garamond, "EB Garamond", serif',
                    '&:hover': {
                      background: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 61, 122, 0.15)',
                      transform: 'translateY(-1px)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                />
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Footer with Copyright */}
      <Box
        sx={{
          py: 4,
          borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
          backgroundColor: bgColor,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="body2"
            sx={{
              color: darkBlueText,
              fontSize: '0.9rem',
              fontFamily: 'Garamond, "EB Garamond", serif',
              fontWeight: 400,
            }}
          >
            © 2025 Iris Technology Solutions
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default GammaHomepage;
